import React, { useCallback, useMemo } from "react";
import { SpreadsheetGrid } from "@components/spreadsheet/SpreadsheetGrid";
import {
    appendSpreadsheetActionsColumn,
    isSpreadsheetActionsColumn,
} from "@components/spreadsheet/spreadsheetActionsColumn";
import { ColumnDef } from "@components/spreadsheet/types";
import { useEntities } from "@hooks/useEntities";
import { useLastBillNumber } from "@api/bill.api";
import { useLastInvoiceNumber } from "@api/invoice.api";
import { useCounterpartyNameMap } from "@components/counterparty/useCounterpartyNameMap";
import { RemoveSpreadsheetRowButton } from "@components/spreadsheet/RemoveSpreadsheetRowButton";
import { AccrualImporterRow, AccrualMode } from "./types";
import { isFirstRowInGroup, SHARED_FIELDS } from "./useAccrualImporterState";
import { useAccrualImporterColumns } from "./useAccrualImporterColumns";
import { useAccrualImporterChangeHandler } from "./useAccrualImporterChangeHandler";
import { usePrefillInvoiceNumber } from "./usePrefillInvoiceNumber";
import styles from "./AccrualImporterDrawer.module.scss";

interface Props {
    mode: AccrualMode;
    rows: AccrualImporterRow[];
    setRows: React.Dispatch<React.SetStateAction<AccrualImporterRow[]>>;
    createRow: () => AccrualImporterRow;
    addLineItemToGroup: (rowIndex: number) => void;
    removeRow: (rowIndex: number) => void;
    counterpartiesMap?: Map<string, string>;
    open?: boolean;
    minSpareRows?: number;
    showActionsColumn?: boolean;
}

export const AccrualImporterSpreadsheet: React.FC<Props> = ({
    mode,
    rows,
    setRows,
    createRow,
    addLineItemToGroup,
    removeRow,
    counterpartiesMap: counterpartiesMapProp,
    open = true,
    minSpareRows = 30,
    showActionsColumn = true,
}) => {
    const entities = useEntities({ onlyBusiness: true });

    const counterpartiesMapFromQuery = useCounterpartyNameMap();

    const counterpartiesMap =
        counterpartiesMapProp ?? counterpartiesMapFromQuery;

    const columns = useAccrualImporterColumns({
        mode,
        counterpartiesMap,
        entities,
    });

    const gridColumns = useMemo((): ColumnDef[] => {
        if (!showActionsColumn) {
            return columns;
        }
        return appendSpreadsheetActionsColumn(columns, {
            label: "",
            width: 160,
            cellFn: ({ row, rowIdx }) => {
                if (rowIdx >= rows.length) {
                    return null;
                }

                const hasInvoiceNumber = !!row?.invoiceNumber;
                return (
                    <div className={styles.actionsCell}>
                        {hasInvoiceNumber && (
                            <button
                                type="button"
                                className={styles.addLineButton}
                                onClick={() => addLineItemToGroup(rowIdx)}
                            >
                                + add line item
                            </button>
                        )}
                        {rows.length > 1 && (
                            <RemoveSpreadsheetRowButton
                                onClick={() => removeRow(rowIdx)}
                            />
                        )}
                    </div>
                );
            },
        });
    }, [
        addLineItemToGroup,
        columns,
        removeRow,
        rows.length,
        showActionsColumn,
    ]);

    const isCellEditable = useCallback(
        (rowIdx: number, colIdx: number): boolean => {
            const col = gridColumns[colIdx];
            if (!col) {
                return false;
            }
            if (col.field === "_rowNumber") {
                return false;
            }
            if (isSpreadsheetActionsColumn(col)) {
                return false;
            }
            if (rowIdx >= rows.length) {
                return true;
            }
            if (col.field === "amount") {
                return false;
            }
            if (SHARED_FIELDS.some((field) => field === col.field)) {
                return isFirstRowInGroup(rows, rowIdx);
            }
            return true;
        },
        [rows, gridColumns],
    );

    const handleChange = useAccrualImporterChangeHandler({
        setRows,
        columns,
        createRow,
    });

    const isBill = mode === "bill";

    const lastBillNumberQuery = useLastBillNumber({ enabled: isBill && open });
    const lastInvoiceNumberQuery = useLastInvoiceNumber({
        enabled: !isBill && open,
    });
    const activeLastNumberQuery = isBill
        ? lastBillNumberQuery
        : lastInvoiceNumberQuery;
    const lastServerInvoiceNumber = activeLastNumberQuery.data ?? null;
    const isLastServerNumberReady = activeLastNumberQuery.isFetched;

    const handleSelectedCellChange = usePrefillInvoiceNumber({
        mode,
        columns,
        setRows,
        lastServerInvoiceNumber,
        isLastServerNumberReady,
    });

    return (
        <SpreadsheetGrid
            className={styles.grid}
            data={rows}
            columns={gridColumns}
            onChange={handleChange}
            onSelectedCellChange={handleSelectedCellChange}
            isCellEditable={isCellEditable}
            minSpareRows={minSpareRows}
            noOuterBorder
            stickyHeader
            showTotalsRow
            overflow="auto"
            lightNotEditableCells
        />
    );
};
